/**
 * Park Manager Portal - PPM Schedule Page
 * Mobile-first view of planned preventive maintenance tasks for the park
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, 
  AlertTriangle, 
  Clock,
  CheckCircle2,
  Shield,
  Wrench,
  Filter,
  ChevronRight
} from 'lucide-react';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'sonner';
import { PortalLayout } from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Overdue': 'bg-red-100 text-red-700 border-red-200',
  'Due': 'bg-amber-100 text-amber-700 border-amber-200',
  'Upcoming': 'bg-blue-100 text-blue-700 border-blue-200',
  'Completed': 'bg-green-100 text-green-700 border-green-200',
  'Pending': 'bg-slate-100 text-slate-700 border-slate-200',
};

const CATEGORY_ICONS = {
  'Fire Safety': Shield,
  'Electrical': Wrench,
  'Gas': Wrench,
  'Water Hygiene': Wrench,
  'HVAC': Wrench,
  'Structural': Wrench,
  'default': Calendar
};

export default function PortalPPMPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState({ overdue: 0, due: 0, upcoming: 0 });

  const fetchPPMTasks = async (status = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/park-portal/ppm`;
      if (status && status !== 'all') {
        url += `?status=${status}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch PPM tasks');
      const data = await res.json();
      
      // Calculate stats and categorize
      const today = new Date();
      const thirtyDays = addDays(today, 30);
      
      let overdue = 0, due = 0, upcoming = 0;
      
      const categorized = data.map(task => {
        const dueDate = task.next_due_date ? parseISO(task.next_due_date) : null;
        let displayStatus = task.status || 'Pending';
        
        if (dueDate && task.status !== 'Completed') {
          if (isBefore(dueDate, today)) {
            displayStatus = 'Overdue';
            overdue++;
          } else if (isBefore(dueDate, thirtyDays)) {
            displayStatus = 'Due';
            due++;
          } else {
            displayStatus = 'Upcoming';
            upcoming++;
          }
        }
        
        return { ...task, displayStatus };
      });
      
      setStats({ overdue, due, upcoming });
      setTasks(categorized);
    } catch (error) {
      toast.error('Failed to load PPM schedule');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPPMTasks(activeTab);
  }, [activeTab]);

  const filteredTasks = tasks.filter(task => {
    if (activeTab === 'all') return true;
    if (activeTab === 'overdue') return task.displayStatus === 'Overdue';
    if (activeTab === 'due') return task.displayStatus === 'Due';
    if (activeTab === 'upcoming') return task.displayStatus === 'Upcoming';
    return true;
  });

  const getCategoryIcon = (category) => {
    const IconComponent = CATEGORY_ICONS[category] || CATEGORY_ICONS['default'];
    return IconComponent;
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="p-4 pb-20 md:p-6 md:pb-6">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-[#1F7A78]" />
            PPM Schedule
          </h1>
          <p className="text-sm text-slate-500 mt-1">Planned Preventive Maintenance tasks</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Card 
            className={`border-0 shadow-sm cursor-pointer transition-all ${activeTab === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setActiveTab('overdue')}
            data-testid="ppm-overdue-stat"
          >
            <CardContent className="p-3 text-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-red-600">{stats.overdue}</p>
              <p className="text-xs text-slate-500">Overdue</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-0 shadow-sm cursor-pointer transition-all ${activeTab === 'due' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setActiveTab('due')}
            data-testid="ppm-due-stat"
          >
            <CardContent className="p-3 text-center">
              <Clock className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-amber-600">{stats.due}</p>
              <p className="text-xs text-slate-500">Due Soon</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-0 shadow-sm cursor-pointer transition-all ${activeTab === 'upcoming' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setActiveTab('upcoming')}
            data-testid="ppm-upcoming-stat"
          >
            <CardContent className="p-3 text-center">
              <CheckCircle2 className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-blue-600">{stats.upcoming}</p>
              <p className="text-xs text-slate-500">Upcoming</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="mb-4 overflow-x-auto hide-scrollbar">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0">
              <TabsTrigger value="all" className="flex-1 sm:flex-none">All</TabsTrigger>
              <TabsTrigger value="overdue" className="flex-1 sm:flex-none text-red-600">
                Overdue {stats.overdue > 0 && `(${stats.overdue})`}
              </TabsTrigger>
              <TabsTrigger value="due" className="flex-1 sm:flex-none text-amber-600">
                Due {stats.due > 0 && `(${stats.due})`}
              </TabsTrigger>
              <TabsTrigger value="upcoming" className="flex-1 sm:flex-none">Upcoming</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Task List */}
        <div className="space-y-2">
          {filteredTasks.length > 0 ? (
            filteredTasks.map((task) => {
              const IconComponent = getCategoryIcon(task.category);
              return (
                <Card 
                  key={task.id}
                  className={`border-0 shadow-sm ${task.displayStatus === 'Overdue' ? 'border-l-4 border-l-red-500' : ''}`}
                  data-testid={`ppm-task-${task.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        task.displayStatus === 'Overdue' ? 'bg-red-50' :
                        task.displayStatus === 'Due' ? 'bg-amber-50' :
                        'bg-slate-50'
                      }`}>
                        <IconComponent className={`h-5 w-5 ${
                          task.displayStatus === 'Overdue' ? 'text-red-500' :
                          task.displayStatus === 'Due' ? 'text-amber-500' :
                          'text-slate-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-medium text-sm truncate">{task.task_name}</h3>
                          <Badge className={`text-xs ml-2 shrink-0 ${STATUS_COLORS[task.displayStatus] || STATUS_COLORS['Pending']}`}>
                            {task.displayStatus}
                          </Badge>
                        </div>
                        
                        {task.asset_name && (
                          <p className="text-xs text-slate-600 mb-1">{task.asset_name}</p>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {task.unit_pitch && (
                            <span className="bg-slate-100 px-2 py-0.5 rounded">{task.unit_pitch}</span>
                          )}
                          {task.category && (
                            <span className="text-slate-500">{task.category}</span>
                          )}
                          {task.frequency && (
                            <span className="text-slate-400">{task.frequency}</span>
                          )}
                          {task.is_legal_requirement && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Legal</Badge>
                          )}
                        </div>
                        
                        {task.next_due_date && (
                          <div className="mt-2 flex items-center gap-1 text-xs">
                            <Calendar className="h-3 w-3 text-slate-400" />
                            <span className={`${
                              task.displayStatus === 'Overdue' ? 'text-red-600 font-medium' :
                              task.displayStatus === 'Due' ? 'text-amber-600' :
                              'text-slate-500'
                            }`}>
                              Due: {format(parseISO(task.next_due_date), 'dd MMM yyyy')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No PPM tasks found</p>
              <p className="text-sm">
                {activeTab === 'overdue' ? 'Great! No overdue tasks' :
                 activeTab === 'due' ? 'No tasks due in the next 30 days' :
                 'No scheduled maintenance tasks'}
              </p>
            </div>
          )}
        </div>

        {/* Info Card */}
        <Card className="border-0 shadow-sm mt-6 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 text-sm">About PPM</h4>
                <p className="text-xs text-blue-700 mt-1">
                  Planned Preventive Maintenance ensures your park meets all legal compliance requirements. 
                  Tasks marked as "Legal" are mandatory and must be completed on schedule.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
